/**
 * B709 leg 2 — the member portal (`/me/membership`, Figma 643:2): the pure
 * half. It reads the buyer's OWN members (`membership.list` under the B632
 * session — the §11 owner fence) and each one's plan (`membership.plan.get`),
 * and says only what those reads hold. What the frame draws that has no buyer
 * door or no model yet — the Wallet buttons, face entry, gifts given, the
 * saved card's last four, UPGRADE / EDIT, "Change card ›", the tax receipt —
 * is left out, and the renewal switch is shut with where to change it.
 * B703 (a): RENEW EARLY rides the buyer's own `membership.renew`, priced as
 * the door prices it (the renewal tier alone — a queued downgrade's).
 */
import type { StageTone } from "@transact/ui";
import { formatMoney, type Money } from "./money.js";

/* ------------------------------------------------------------------ reads */

export type PortalStatus = "ACTIVE" | "GRACE" | "LAPSED" | "CANCELLED" | "GIFT_UNREDEEMED";

export interface PortalCard {
  readonly cardId: string;
  readonly holder: string;
  readonly issuedAt: number;
  readonly validFrom: number;
  readonly validUntil: number;
  readonly revokedAt?: number;
  readonly idCheckedAt?: number;
}

/** The slice of a `membership.list` member the portal reads. */
export interface PortalMember {
  readonly memberNumber: string;
  readonly planId: string;
  readonly tierId: string;
  readonly email?: string;
  readonly term: { readonly start: number; readonly end: number };
  readonly status: PortalStatus;
  readonly namedAdults: readonly { readonly name: string }[];
  readonly householdCount: number;
  readonly addOns: readonly string[];
  readonly renewal: { readonly method: "MANUAL" | "AUTO"; readonly pendingTierId?: string };
  readonly allowances: { readonly guestPasses: number; readonly coupons: number };
  readonly cards: readonly PortalCard[];
  readonly createdAt: number;
  readonly cancelledAt?: number;
  /** B709 (a3) — a gift's voucher (absent on anything bought for oneself). */
  readonly gift?: { readonly voucherCode: string; readonly expiresAt?: number; readonly redeemedAt?: number };
}

export interface PortalTier {
  readonly tierId: string;
  readonly name: string;
  readonly price: Money;
  readonly covers: { readonly namedAdults?: number; readonly householdChildren: { readonly max: number; readonly ageBand?: { readonly min: number; readonly max: number } } };
  readonly benefits: { readonly guestPasses: number; readonly coupons: number };
  readonly addOns: readonly { readonly addOnId: string; readonly name: string; readonly price: Money }[];
}

/** The slice of a `membership.plan.get` plan the portal reads. */
export interface PortalPlan {
  readonly planId: string;
  readonly name: string;
  readonly kind: "ANNUAL" | "MONTHLY";
  readonly tiers: readonly PortalTier[];
}

const STATUSES = new Set<string>(["ACTIVE", "GRACE", "LAPSED", "CANCELLED", "GIFT_UNREDEEMED"]);
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isStr = (v: unknown): v is string => typeof v === "string" && v !== "";

function memberOf(v: unknown): PortalMember | undefined {
  if (!isObj(v) || !isStr(v["memberNumber"]) || !isStr(v["planId"]) || !isStr(v["tierId"]) || !STATUSES.has(String(v["status"]))) return undefined;
  const term = v["term"];
  if (!isObj(term) || !isNum(term["start"]) || !isNum(term["end"])) return undefined;
  const allowances = isObj(v["allowances"]) ? v["allowances"] : {};
  const renewal = isObj(v["renewal"]) && v["renewal"]["method"] === "AUTO" ? "AUTO" : "MANUAL";
  const queued = isObj(v["renewal"]) && isStr(v["renewal"]["pendingTierId"]) ? v["renewal"]["pendingTierId"] : undefined;
  const cards = (Array.isArray(v["cards"]) ? v["cards"] : []).filter(
    (c): c is PortalCard => isObj(c) && isStr(c["cardId"]) && isStr(c["holder"]) && isNum(c["validUntil"]),
  );
  return {
    memberNumber: v["memberNumber"],
    planId: v["planId"],
    tierId: v["tierId"],
    ...(isStr(v["email"]) ? { email: v["email"] } : {}),
    term: { start: term["start"], end: term["end"] },
    status: v["status"] as PortalStatus,
    namedAdults: (Array.isArray(v["namedAdults"]) ? v["namedAdults"] : []).filter((a): a is { name: string } => isObj(a) && isStr(a["name"])),
    householdCount: isNum(v["householdCount"]) ? v["householdCount"] : 0,
    addOns: (Array.isArray(v["addOns"]) ? v["addOns"] : []).filter(isStr),
    renewal: { method: renewal, ...(queued !== undefined ? { pendingTierId: queued } : {}) },
    ...(isObj(v["gift"]) && isStr(v["gift"]["voucherCode"])
      ? { gift: { voucherCode: v["gift"]["voucherCode"], ...(isNum(v["gift"]["expiresAt"]) ? { expiresAt: v["gift"]["expiresAt"] } : {}), ...(isNum(v["gift"]["redeemedAt"]) ? { redeemedAt: v["gift"]["redeemedAt"] } : {}) } }
      : {}),
    allowances: { guestPasses: isNum(allowances["guestPasses"]) ? allowances["guestPasses"] : 0, coupons: isNum(allowances["coupons"]) ? allowances["coupons"] : 0 },
    cards,
    createdAt: isNum(v["createdAt"]) ? v["createdAt"] : term["start"],
    ...(isNum(v["cancelledAt"]) ? { cancelledAt: v["cancelledAt"] } : {}),
  };
}

/** `membership.list`'s answer, narrowed; a malformed member is dropped, never thrown on. */
export function portalMembers(res: unknown): readonly PortalMember[] {
  const list = isObj(res) && Array.isArray(res["members"]) ? res["members"] : [];
  return list.map(memberOf).filter((m): m is PortalMember => m !== undefined);
}

/** `membership.plan.get`'s answer, narrowed; anything unreadable is no plan (the card stands on the member alone). */
export function portalPlan(res: unknown): PortalPlan | undefined {
  const p = isObj(res) ? res["plan"] : undefined;
  if (!isObj(p) || !isStr(p["planId"]) || !Array.isArray(p["tiers"])) return undefined;
  const tiers = p["tiers"].filter(
    (t): t is PortalTier =>
      isObj(t) && isStr(t["tierId"]) && isStr(t["name"]) && isObj(t["price"]) && isObj(t["benefits"]) && isObj(t["covers"]) && Array.isArray(t["addOns"]),
  );
  return { planId: p["planId"], name: isStr(p["name"]) ? p["name"] : "", kind: p["kind"] === "MONTHLY" ? "MONTHLY" : "ANNUAL", tiers };
}

/* ------------------------------------------------------------------ faces */

export interface PortalRow {
  readonly label: string;
  readonly value: string;
}

export interface PortalAllowance {
  readonly label: string;
  /** "2 of 4 left" — or "2 left" when the plan (the total) is unreadable. */
  readonly words: string;
  /** What is left over what the tier grants; absent without the total. */
  readonly fraction?: number;
}

/** B703 (a) — the RENEW door: what the press asks `membership.renew` for (the term it renews, the total the door charges). */
export interface PortalRenew {
  readonly label: "Renew early" | "Renew now";
  readonly amount: Money;
  readonly termEnd: number;
  readonly note: string;
  /** B828 — the span one renewal adds (the card step says "your next month" on a monthly plan). */
  readonly termWord: "year" | "month";
}

export interface MembershipFace {
  readonly memberNumber: string;
  readonly tierName: string;
  readonly chip: { readonly word: string; readonly tone: StageTone };
  /** "Valid to Oct 5, 2027 · member since 2024 · M10482". */
  readonly facts: string;
  readonly cards: readonly { readonly cardId: string; readonly name: string; readonly note: string }[];
  readonly cardsNote: string;
  /** What the card says while no card is printed. */
  readonly noCardsNote: string;
  readonly allowancesLabel: string;
  readonly allowances: readonly PortalAllowance[];
  readonly allowancesNote?: string;
  /** Absent once the membership is over (lapsed, cancelled) or not yet redeemed. */
  readonly renewal?: { readonly auto: boolean; readonly rows: readonly PortalRow[]; readonly note: string; readonly shutWhy: string; readonly renew?: PortalRenew };
  readonly household: { readonly rows: readonly PortalRow[]; readonly note: string };
  /** B709 (a3) — an unredeemed gift: who its tier covers, for the redeem panel. */
  readonly gift?: { readonly adults: number; readonly childrenMax: number; readonly childrenHint?: string; readonly termWord: "year" | "month" };
}

const CHIPS: Readonly<Record<PortalStatus, MembershipFace["chip"]>> = {
  ACTIVE: { word: "ACTIVE", tone: "confirmed" },
  GRACE: { word: "RENEW NOW", tone: "due" },
  LAPSED: { word: "LAPSED", tone: "lapsed" },
  CANCELLED: { word: "CANCELLED", tone: "void" },
  GIFT_UNREDEEMED: { word: "GIFT · NOT REDEEMED", tone: "info" },
};
const LIVE = new Set<PortalStatus>(["ACTIVE", "GRACE", "GIFT_UNREDEEMED"]);

const first = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/** "Oct 5, 2027" on the VENUE's calendar (B659: never the device's zone). */
const dayWords = (ms: number, timeZone: string): string => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone }).format(ms);
const yearWords = (ms: number, timeZone: string): string => new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone }).format(ms);

function faceOf(m: PortalMember, plan: PortalPlan | undefined, timeZone: string | undefined): MembershipFace {
  const tier = plan?.tiers.find((t) => t.tierId === m.tierId);
  /* the term's end is an instant (the next day's midnight): its last day is the one before */
  const last = timeZone !== undefined ? dayWords(m.term.end - 1, timeZone) : undefined;
  const live = LIVE.has(m.status);
  const validity =
    last === undefined
      ? undefined
      : m.status === "CANCELLED"
        ? m.cancelledAt !== undefined
          ? `Cancelled ${dayWords(m.cancelledAt, timeZone!)}`
          : undefined
        : m.status === "ACTIVE"
          ? `Valid to ${last}`
          : `Ended ${last}`;
  const since = timeZone !== undefined ? `member since ${yearWords(m.createdAt, timeZone)}` : undefined;
  // B709 (a3) — an unredeemed gift has no term yet: it starts the day it is redeemed.
  const unredeemed = m.status === "GIFT_UNREDEEMED";
  const redeemBy = unredeemed && m.gift?.expiresAt !== undefined && timeZone !== undefined ? `redeem by ${dayWords(m.gift.expiresAt, timeZone)}` : undefined;

  const total = (left: number, granted: number | undefined, label: string): PortalAllowance | undefined => {
    if (granted !== undefined) return granted > 0 ? { label, words: `${String(left)} of ${String(granted)} left`, fraction: Math.min(1, left / granted) } : undefined;
    return left > 0 ? { label, words: `${String(left)} left` } : undefined;
  };
  const allowances = [total(m.allowances.guestPasses, tier?.benefits.guestPasses, "Guest passes"), total(m.allowances.coupons, tier?.benefits.coupons, "Member coupons")].filter(
    (a): a is PortalAllowance => a !== undefined,
  );

  const addOns = tier === undefined ? [] : m.addOns.map((id) => tier.addOns.find((a) => a.addOnId === id)).filter((a): a is PortalTier["addOns"][number] => a !== undefined);
  const amount =
    tier === undefined
      ? undefined
      : `${formatMoney({ amount: tier.price.amount + addOns.reduce((s, a) => s + a.price.amount, 0), currency: tier.price.currency })} ${plan?.kind === "MONTHLY" ? "a month" : "a year"}`;
  const auto = m.renewal.method === "AUTO";
  // B703 (a) — the door's price: the renewal tier (a queued downgrade's) + the add-ons it still sells, which renew
  // with it (the design seat's 10-10 default; the server's renewalPriceOf) — the Amount row's own sum.
  const next = plan?.tiers.find((t) => t.tierId === (m.renewal.pendingTierId ?? m.tierId));
  const nextPrice =
    next === undefined
      ? undefined
      : { amount: m.addOns.reduce((sum, id) => sum + (next.addOns.find((a) => a.addOnId === id)?.price.amount ?? 0), next.price.amount), currency: next.price.currency };
  const span = plan?.kind === "MONTHLY" ? "month" : "year";
  const renew: PortalRenew | undefined =
    next === undefined || nextPrice === undefined || nextPrice.amount <= 0 || (m.status !== "ACTIVE" && m.status !== "GRACE")
      ? undefined
      : {
          label: m.status === "GRACE" ? "Renew now" : "Renew early",
          amount: nextPrice,
          termEnd: m.term.end,
          termWord: span,
          note:
            m.status === "GRACE"
              ? `Renewing adds a ${span} from the day it ended — ${formatMoney(nextPrice)} today.`
              : `Renewing early adds a ${span} to your current expiry — ${formatMoney(nextPrice)} today.`,
        };
  const renewal =
    m.status === "ACTIVE" || m.status === "GRACE"
      ? {
          auto,
          rows: [
            ...(last !== undefined ? [auto ? { label: "Renews", value: `${last} · automatically` } : { label: "Ends", value: last }] : []),
            ...(amount !== undefined ? [{ label: "Amount", value: amount }] : []),
          ],
          note: auto
            ? "We'll email you 30, 7 and 1 days before it renews."
            : renew !== undefined
              ? "Renew here or at any ticket window before then — your member number stays the same."
              : "Renew at any ticket window before then — your member number stays the same.",
          shutWhy: "Turn it on or off at any ticket window — online changes are on their way.",
          ...(renew !== undefined ? { renew } : {}),
        }
      : undefined;

  const children = m.householdCount > 0 ? `${String(m.householdCount)}${tier?.covers.householdChildren.ageBand !== undefined ? ` (under ${String(tier.covers.householdChildren.ageBand.max + 1)})` : ""}` : "None";
  const household: PortalRow[] = [
    { label: "Named adults", value: unredeemed && m.namedAdults.length === 0 ? "Named when you redeem" : m.namedAdults.map((a) => first(a.name)).join(", ") },
    ...(m.householdCount > 0 || (tier?.covers.householdChildren.max ?? 0) > 0 ? [{ label: "Children", value: children }] : []),
    ...(addOns.length > 0 ? [{ label: "Add-ons", value: addOns.map((a) => a.name).join(" · ") }] : []),
  ];

  return {
    memberNumber: m.memberNumber,
    tierName: tier?.name ?? "Your membership",
    chip: CHIPS[m.status],
    facts: (unredeemed ? ["A gift — it starts the day you redeem it", redeemBy, m.memberNumber] : [validity, since, m.memberNumber]).filter((s): s is string => s !== undefined).join(" · "),
    cards: m.cards
      .filter((c) => c.revokedAt === undefined)
      .map((c) => ({ cardId: c.cardId, name: first(c.holder), note: c.idCheckedAt !== undefined ? "ID checked" : "Card ready" })),
    noCardsNote: unredeemed ? "Your cards are issued the day you redeem it." : "Your cards aren't printed yet — any ticket window issues them.",
    cardsNote: "Each card's QR code is in your welcome email. Lost a card? Any ticket window re-issues it — the old one stops working at once.",
    allowancesLabel: plan === undefined ? "This term" : plan.kind === "MONTHLY" ? "This month" : "This year",
    // An unredeemed gift's allowances are granted the day it starts (redeemGiftMember refills them).
    allowances: unredeemed ? [] : allowances,
    ...(live && !unredeemed && m.allowances.guestPasses > 0 ? { allowancesNote: "Guest passes are used at the ticket window — show your card and say how many." } : {}),
    ...(renewal !== undefined ? { renewal } : {}),
    household: {
      rows: household,
      note: unredeemed ? "You name who it covers when you redeem it; the names go on the cards." : "Names are locked until renewal — ask at any ticket window to change one sooner.",
    },
    ...(unredeemed
      ? {
          gift: {
            adults: tier?.covers.namedAdults ?? 1,
            childrenMax: tier?.covers.householdChildren.max ?? 0,
            ...((tier?.covers.householdChildren.max ?? 0) > 0
              ? {
                  childrenHint: `Up to ${String(tier!.covers.householdChildren.max)}${tier!.covers.householdChildren.ageBand === undefined ? "" : `, ages ${String(tier!.covers.householdChildren.ageBand.min)}–${String(tier!.covers.householdChildren.ageBand.max)}`} — they come in with a named adult.`,
                }
              : {}),
            termWord: plan?.kind === "MONTHLY" ? ("month" as const) : ("year" as const),
          },
        }
      : {}),
  };
}

/** Every membership on the email: the live ones first, then the newest term. */
export function membershipFaces(
  members: readonly PortalMember[],
  plans: Readonly<Record<string, PortalPlan | undefined>>,
  opts: { readonly timeZone: string | undefined },
): readonly MembershipFace[] {
  return [...members]
    .sort((a, b) => Number(LIVE.has(b.status)) - Number(LIVE.has(a.status)) || b.term.end - a.term.end)
    .map((m) => faceOf(m, plans[m.planId], opts.timeZone));
}

/** "Hi Priya" — the first named adult of the live (else newest) membership. */
export function memberGreeting(members: readonly PortalMember[]): string | undefined {
  const top = [...members].sort((a, b) => Number(LIVE.has(b.status)) - Number(LIVE.has(a.status)) || b.term.end - a.term.end)[0];
  const name = top?.namedAdults[0]?.name;
  return name === undefined ? undefined : first(name);
}

/* ------------------------------------------------------------ B703 (a) renew */

/** Where the card page sends the buyer back: the portal, naming the membership it was renewing. */
export function renewReturnUrl(origin: string, tenantId: string, memberNumber: string): string {
  return `${origin}/me/membership?tenant=${encodeURIComponent(tenantId)}&renew=${encodeURIComponent(memberNumber)}`; // vocab-ok: URL query keys, never rendered
}

export type RenewAnswer =
  | { readonly kind: "renewed"; readonly termEnd: number }
  | { readonly kind: "card"; readonly setupUrl?: string; readonly setupClientSecret?: string; readonly refused?: string }
  | { readonly kind: "refused"; readonly message: string };

export const RENEW_NOT_CONFIRMED = "The membership couldn't be renewed just now. Nothing was charged — try again in a moment.";

/** `membership.renew`'s answer, read defensively. */
export function renewAnswer(res: unknown): RenewAnswer {
  const v = isObj(res) ? res : {};
  const member = v["member"];
  const term = isObj(member) ? member["term"] : undefined;
  if (isObj(term) && isNum(term["end"])) return { kind: "renewed", termEnd: term["end"] };
  if (isStr(v["setupUrl"]) || isStr(v["setupClientSecret"]))
    return {
      kind: "card",
      ...(isStr(v["setupUrl"]) ? { setupUrl: v["setupUrl"] } : {}),
      ...(isStr(v["setupClientSecret"]) ? { setupClientSecret: v["setupClientSecret"] } : {}), // B823
      ...(isStr(v["refused"]) ? { refused: v["refused"] } : {}),
    };
  if (isStr(v["refused"])) return { kind: "refused", message: v["refused"] };
  return { kind: "refused", message: RENEW_NOT_CONFIRMED };
}

/** "Renewed — your membership now runs to Oct 5, 2028." (the last day, on the venue's calendar). */
export function renewedWords(termEnd: number, timeZone: string | undefined): string {
  return timeZone === undefined ? "Renewed — your new term is on your membership below." : `Renewed — your membership now runs to ${dayWords(termEnd - 1, timeZone)}.`;
}

/* ----------------------------------------------------------- B709 (a3) gift */

export interface GiftDraft {
  readonly adult1: string;
  readonly adult2: string;
  readonly children: number;
}

/** Why REDEEM MY GIFT would be refused; undefined = ready. */
export function giftRedeemWhy(d: GiftDraft): string | undefined {
  return d.adult1.trim() === "" ? "Name the first adult. Their name goes on their card." : undefined;
}

/** `membership.gift.redeem`'s request from the buyer's own session: by member number (owner-fenced server-side). */
export function giftRedeemRequest(memberNumber: string, d: GiftDraft): { readonly memberNumber: string; readonly namedAdults: readonly { readonly name: string }[]; readonly householdCount: number } {
  const names = [d.adult1, d.adult2].map((n) => n.trim()).filter((n) => n !== "");
  return { memberNumber, namedAdults: names.map((name) => ({ name })), householdCount: d.children };
}

/** "Redeemed — your membership runs to Oct 10, 2027. …" (the last day, on the venue's calendar). */
export function redeemedWords(termEnd: number, timeZone: string | undefined): string {
  const runs = timeZone === undefined ? "Redeemed — your membership has started." : `Redeemed — your membership runs to ${dayWords(termEnd - 1, timeZone)}.`;
  return `${runs} Your cards are in your email.`;
}
